import {
  windowsForRange,
  type ActivityRangeKey,
  type AdminGcpStats,
  type AdminLlmCostRow,
  type AdminPlatformWindow,
  type AdminStatsSnapshot,
  type PlatformTotals,
} from '@djgg/creations';

export type AdminStatsSnapshotRow = AdminStatsSnapshot & { id: string };
export type CostDimension = 'lane' | 'provider' | 'model' | 'task';

export const COST_LANE_LABELS: Record<AdminLlmCostRow['lane'], string> = {
  'game-v1': 'V1 game generation',
  iteration: 'Normal iterations',
  assets: 'Asset generation',
  concept: 'Concept analysis',
  other: 'Other model work',
};

export interface CostGroup {
  key: string;
  label: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface WindowPoint {
  start: number;
  llmCostUsd: number | null;
  requests: number | null;
  errors: number | null;
  queryCalls: number | null;
  productEvents: number | null;
}

export interface AdminStatsAggregate {
  selected: AdminStatsSnapshotRow[];
  latest: AdminStatsSnapshotRow;
  /** Newest row whose digest actually ran (ok or error); most windows skip it. */
  digest: AdminStatsSnapshotRow | null;
  baseline: AdminStatsSnapshotRow | null;
  coveredHours: number;
  llmRows: AdminLlmCostRow[];
  actions: Array<{ action: string; count: number }>;
  logs: AdminGcpStats['logCounts'];
  recentLogs: NonNullable<AdminGcpStats['recentLogs']>;
  services: AdminGcpStats['services'];
  deploys: AdminGcpStats['deploys'];
  product: AdminPlatformWindow | null;
  productTotals: PlatformTotals | null;
  productDeltas: {
    pageViews: number | null;
    totalPlays: number | null;
    gameViews: number | null;
    assetsAttached: number | null;
  };
  github: {
    prsMerged: number;
    commits: number;
    deployCandidates: number;
    mergedPrs: NonNullable<AdminStatsSnapshot['quant']['github']>['mergedPrs'];
    openPrs: NonNullable<NonNullable<AdminStatsSnapshot['quant']['github']>['openPrs']>;
  };
  queries: {
    available: boolean;
    totalCalls: number | null;
    slowCalls: number | null;
    avgPerHour: number | null;
    peakWindowCalls: number | null;
    burstRatio: number | null;
    maxExecMs: number | null;
    dbSizeBytes: number | null;
    dbGrowthBytes: number | null;
    cacheHitRatio: number | null;
    statsResetAt: number | null;
    topSlow: NonNullable<
      NonNullable<AdminStatsSnapshot['quant']['postgres']>['dbQueries']
    >['topSlow'];
    topQueries: NonNullable<NonNullable<
      NonNullable<AdminStatsSnapshot['quant']['postgres']>['dbQueries']
    >['topQueries']>;
  };
  points: WindowPoint[];
}

const addMap = (map: Map<string, number>, key: string, value: number) =>
  map.set(key, (map.get(key) ?? 0) + value);

function counterDelta(current: number | undefined, baseline: number | undefined): number | null {
  if (current == null || baseline == null || current < baseline) return null;
  return current - baseline;
}

function signedDelta(current: number | undefined, baseline: number | undefined): number | null {
  if (current == null || baseline == null) return null;
  return current - baseline;
}

function totalDelta(
  latest: PlatformTotals | undefined,
  baseline: PlatformTotals | undefined,
  key: keyof PlatformTotals,
): number | null {
  return counterDelta(latest?.[key], baseline?.[key]);
}

function queryDelta(
  newer: AdminStatsSnapshotRow | undefined,
  older: AdminStatsSnapshotRow | undefined,
  key: 'totalCalls' | 'slowCalls',
): number | null {
  const a = newer?.quant.postgres?.dbQueries;
  const b = older?.quant.postgres?.dbQueries;
  if (!a?.available || !b?.available) return null;
  if (a.statsResetAt !== b.statsResetAt) return null;
  return counterDelta(a[key], b[key]);
}

function sumProduct(
  rows: AdminStatsSnapshotRow[],
): { value: AdminPlatformWindow | null; covered: number } {
  const value: AdminPlatformWindow = {
    gamesCreated: 0,
    nativeGamesAdded: 0,
    v1GamesLive: 0,
    completedForks: 0,
    gameIterations: 0,
    assetVariantsGenerated: 0,
    multiplayerMatches: 0,
    newUsers: 0,
    newSessions: 0,
    likes: 0,
    comments: 0,
  };
  let covered = 0;
  for (const row of rows) {
    const activity = row.quant.postgres?.platformWindow;
    if (!activity) continue;
    covered += 1;
    for (const key of Object.keys(value) as Array<keyof AdminPlatformWindow>) {
      // Schema-v2 snapshots predate nativeGamesAdded, so treat a missing
      // counter as zero when an all-time range crosses the schema boundary.
      value[key] += activity[key] ?? 0;
    }
  }
  return { value: covered > 0 ? value : null, covered };
}

function aggregateServices(rows: AdminStatsSnapshotRow[]): AdminGcpStats['services'] {
  const byService = new Map<string, AdminGcpStats['services'][number]>();
  for (const snapshot of rows) {
    for (const service of snapshot.quant.gcp?.services ?? []) {
      const current = byService.get(service.service) ?? {
        service: service.service,
        requestCount: 0,
        errorCount: 0,
        latencyMsP50: null,
        latencyMsP95: null,
        latencyMsP99: null,
        cpuUtilP95: null,
        memUtilP95: null,
      };
      current.requestCount += service.requestCount;
      current.errorCount += service.errorCount;
      for (const key of [
        'latencyMsP50',
        'latencyMsP95',
        'latencyMsP99',
        'cpuUtilP95',
        'memUtilP95',
      ] as const) {
        const value = service[key];
        if (value != null) current[key] = Math.max(current[key] ?? value, value);
      }
      byService.set(service.service, current);
    }
  }
  return [...byService.values()].sort((a, b) => b.requestCount - a.requestCount);
}

export function groupCosts(rows: AdminLlmCostRow[], dimension: CostDimension): CostGroup[] {
  const groups = new Map<string, CostGroup>();
  for (const row of rows) {
    const key = row[dimension];
    const group = groups.get(key) ?? {
      key,
      label: dimension === 'lane' ? COST_LANE_LABELS[row.lane] : key,
      calls: 0,
      inputTokens: 0,
      outputTokens: 0,
      costUsd: 0,
    };
    group.calls += row.calls;
    group.inputTokens += row.inputTokens;
    group.outputTokens += row.outputTokens;
    group.costUsd += row.costUsd;
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => b.costUsd - a.costUsd);
}

export function aggregateAdminStats(
  snapshots: AdminStatsSnapshotRow[],
  range: ActivityRangeKey,
): AdminStatsAggregate | null {
  const windowCount = windowsForRange(range);
  const selected = windowCount == null ? snapshots : snapshots.slice(0, windowCount);
  const latest = selected[0];
  if (!latest) return null;
  const baseline = snapshots[selected.length] ?? null;
  const digest =
    snapshots.find((snapshot) => {
      const status = snapshot.provenance.llm?.status;
      return status === 'ok' || status === 'error';
    }) ?? null;
  const llmRows = selected.flatMap((snapshot) => snapshot.quant.postgres?.llmCosts ?? []);

  const actionMap = new Map<string, number>();
  const logMap = new Map<string, number>();
  const deploys: AdminGcpStats['deploys'] = [];
  const recentLogs: NonNullable<AdminGcpStats['recentLogs']> = [];
  let prsMerged = 0;
  let commits = 0;
  let deployCandidates = 0;
  const mergedPrs: AdminStatsAggregate['github']['mergedPrs'] = [];
  for (const snapshot of selected) {
    for (const action of snapshot.quant.postgres?.actionLogWindow ?? []) {
      addMap(actionMap, action.action, action.count);
    }
    for (const log of snapshot.quant.gcp?.logCounts ?? []) {
      addMap(logMap, `${log.service}\u0000${log.severity}`, log.count);
    }
    deploys.push(...(snapshot.quant.gcp?.deploys ?? []));
    recentLogs.push(...(snapshot.quant.gcp?.recentLogs ?? []));
    const github = snapshot.quant.github;
    if (github) {
      prsMerged += github.prsMergedWindow;
      commits += github.commitsWindow;
      deployCandidates += github.deployCandidateMerges;
      mergedPrs.push(...github.mergedPrs);
    }
  }

  const queryWindows = selected
    .map((snapshot, index) => queryDelta(snapshot, snapshots[index + 1], 'totalCalls'))
    .filter((value): value is number => value != null);
  const rangeQueryCalls = queryDelta(latest, baseline ?? undefined, 'totalCalls');
  const rangeSlowCalls = queryDelta(latest, baseline ?? undefined, 'slowCalls');
  const queryCoveredHours =
    rangeQueryCalls != null ? selected.length * 4 : queryWindows.length * 4;
  const totalCalls =
    rangeQueryCalls ??
    (queryWindows.length
      ? queryWindows.reduce((sum, value) => sum + value, 0)
      : null);
  const slowCalls =
    rangeSlowCalls ??
    (() => {
      const values = selected
        .map((snapshot, index) => queryDelta(snapshot, snapshots[index + 1], 'slowCalls'))
        .filter((value): value is number => value != null);
      return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
    })();
  const peakWindowCalls = queryWindows.length ? Math.max(...queryWindows) : null;
  const avgWindowCalls = queryWindows.length
    ? queryWindows.reduce((sum, value) => sum + value, 0) / queryWindows.length
    : null;
  const latestPg = latest.quant.postgres;
  const baselinePg = baseline?.quant.postgres;
  const counterBaselinePg =
    baselinePg ??
    (selected.length > 1 ? selected[selected.length - 1].quant.postgres : undefined);
  const cacheBlocks =
    latestPg && baselinePg
      ? {
          hit: counterDelta(latestPg.blksHit, baselinePg.blksHit),
          read: counterDelta(latestPg.blksRead, baselinePg.blksRead),
        }
      : null;
  const cacheDenominator =
    cacheBlocks && cacheBlocks.hit != null && cacheBlocks.read != null
      ? cacheBlocks.hit + cacheBlocks.read
      : 0;
  const { value: product } = sumProduct(selected);

  const chronological = [...selected].reverse();
  const points: WindowPoint[] = chronological.map((snapshot) => {
    const newerIndex = snapshots.indexOf(snapshot);
    const productWindow = snapshot.quant.postgres?.platformWindow;
    return {
      start: snapshot.windowStart,
      llmCostUsd:
        snapshot.quant.postgres?.llmCosts.reduce((sum, row) => sum + row.costUsd, 0) ?? null,
      requests:
        snapshot.quant.gcp?.services.reduce((sum, service) => sum + service.requestCount, 0) ??
        null,
      errors:
        snapshot.quant.gcp?.services.reduce((sum, service) => sum + service.errorCount, 0) ??
        null,
      queryCalls: queryDelta(snapshot, snapshots[newerIndex + 1], 'totalCalls'),
      productEvents: productWindow
        ? Object.values(productWindow).reduce((sum, value) => sum + (value ?? 0), 0)
        : null,
    };
  });

  return {
    selected,
    latest,
    digest,
    baseline,
    coveredHours: selected.length * 4,
    llmRows,
    actions: [...actionMap.entries()]
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count),
    logs: [...logMap.entries()]
      .map(([key, count]) => {
        const [service, severity] = key.split('\u0000');
        return { service, severity, count };
      })
      .sort((a, b) => b.count - a.count),
    recentLogs: recentLogs.sort((a, b) => b.timestamp - a.timestamp),
    services: aggregateServices(selected),
    deploys: deploys.sort((a, b) => b.createTime - a.createTime),
    product,
    productTotals: latestPg?.platformTotals ?? null,
    productDeltas: Object.fromEntries(
      (['pageViews', 'totalPlays', 'gameViews', 'assetsAttached'] as const).map((key) => [
        key,
        range === 'all'
          ? (latestPg?.platformTotals?.[key] ?? null)
          : totalDelta(latestPg?.platformTotals, counterBaselinePg?.platformTotals, key),
      ]),
    ) as AdminStatsAggregate['productDeltas'],
    github: {
      prsMerged,
      commits,
      deployCandidates,
      mergedPrs: mergedPrs.sort((a, b) => b.mergedAt - a.mergedAt),
      openPrs: latest.quant.github?.openPrs ?? [],
    },
    queries: {
      available: queryWindows.length > 0,
      totalCalls,
      slowCalls,
      avgPerHour:
        totalCalls == null || queryCoveredHours === 0 ? null : totalCalls / queryCoveredHours,
      peakWindowCalls,
      burstRatio:
        peakWindowCalls == null || avgWindowCalls == null || avgWindowCalls === 0
          ? null
          : peakWindowCalls / avgWindowCalls,
      maxExecMs: latestPg?.dbQueries?.maxExecMs ?? null,
      dbSizeBytes: latestPg?.dbSizeBytes ?? null,
      dbGrowthBytes: signedDelta(latestPg?.dbSizeBytes, baselinePg?.dbSizeBytes),
      cacheHitRatio:
        cacheBlocks?.hit == null || cacheDenominator === 0
          ? null
          : cacheBlocks.hit / cacheDenominator,
      statsResetAt: latestPg?.dbQueries?.statsResetAt ?? null,
      topSlow: latestPg?.dbQueries?.topSlow ?? [],
      topQueries: latestPg?.dbQueries?.topQueries ?? [],
    },
    points,
  };
}
